import { useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardBody } from "react-bootstrap";
import PostFormView from "../../PostFormView";
import AddButton from "../../../../shared/Buttons/AddButton";
import crudApi from "@serie3/common/api/crudApi";
import listHandler from "@serie3/common/Domain/listHandler";
import MediaplannerGroupEndpoints from "@serie3/mymediaplanner/Modules/Groups/endpoints";
import GroupConfig from "@serie3/common/Modules/Groups/Resources/Endpoints";
import SocialEndpoints from "@serie3/mymediaplanner/Modules/Social/endpoints";
import PostAssistant from "../../PostAssistant";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWandMagicSparkles, faVideo } from "@fortawesome/free-solid-svg-icons";
import GenericKanbanBoard from "../../../../shared/KanbanBoard/GenericKanbanBoard";
import GroupPostMenu from "./GroupPostMenu";
import TelepromterModal from "../../TelepromterModal";
import MegaprompterModal from "../../Megaprompter/MegaprompterModal";
import ShareModal from "../../ShareModal";
import GroupPostViewModal from "../GroupPostViewModal";
import SettingsContext from "@serie3/common/auth/SettingsContext";
import { Roles, normalizeCaps, hasAny } from "@serie3/common/config/RoleRights";

import { sortPostsByType } from "@serie3/mymediaplanner/Modules/Timeline/Domain/sortPosts";

export default function Posts({
  shares,
  setShares,
  group,
  rights,
  deletePost,
}) {
  const { t } = useTranslation();
  const { settings } = useContext(SettingsContext);

  const [editPost, setEditPost] = useState(null);
  const [editShare, setEditShare] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [viewPostId, setViewPostId] = useState(null);
  const [showTeleprompter, setShowTeleprompter] = useState(false);
  const [teleprompterPost, setTeleprompterPost] = useState(null);
  const [showShare, setShowShare] = useState(false);
  const [sharePostId, setSharePostId] = useState(null);
  const [showAssistant, setShowAssistant] = useState(false);
  const [showGenerating, setShowGenerating] = useState(false);
  const [showMegaprompter, setShowMegaprompter] = useState(false);

  // View-only modal state (for non-owners/non-admins)
  const [showView, setShowView] = useState(false);
  const [viewShare, setViewShare] = useState(null);

  // Check whether current user can edit a given share
  const canEdit = useCallback(
    (share) => {
      const caps = normalizeCaps(rights);
      return (
        share?.userinfo?.username === settings?.user?.username ||
        hasAny(caps, [Roles.OWNER, Roles.ADMIN])
      );
    },
    [rights, settings],
  );

  const openPost = useCallback(
    (post, task) => {
      const share = task?.share ?? shares?.find((s) => s?.post?.id === post?.id);
      if (canEdit(share)) {
        setEditPost(post);
        setEditShare(share);
        setViewPostId(post?.id);
        setShowEdit(true);
      } else {
        setViewShare(share);
        setShowView(true);
      }
    },
    [canEdit, shares],
  );

  /**
   * Cards by other members carry the author's avatar in front of the title
   * (hover: who wrote it, and whether you may edit it). Cards you cannot edit
   * are also drawn read-only: dashed edge, muted ground. Your own posts look
   * as before.
   */
  const shareOfTask = useCallback(
    (post, task) => task?.share ?? shares?.find((s) => s?.post?.id === post?.id),
    [shares],
  );

  const getItemProps = useCallback(
    (post, task) => {
      const share = shareOfTask(post, task);
      const me = settings?.user?.username ?? null;
      if (!share) return { currentUsername: me };
      const author = { id: share?.userinfo?.id, name: share?.userinfo?.name, username: share?.userinfo?.username };
      return { readOnly: !canEdit(share), author, currentUsername: me };
    },
    [shareOfTask, canEdit, settings],
  );

  const getShareId = useCallback(
    (share) => String(share?.id ?? share?.post?.id),
    [],
  );

  const replacePostInList = useCallback((list, updatedPost) => {
    return (list ?? []).map((item) =>
      item?.post?.id === updatedPost.id ? { ...item, post: updatedPost } : item,
    );
  }, []);

  const setDataList = useCallback(
    (response) => {
      if (response?.ok) {
        setShares((prev) => replacePostInList(prev, response.data));
      }
    },
    [setShares, replacePostInList],
  );

  const onAdd = async () => {
    const response = await crudApi.justGet(
      MediaplannerGroupEndpoints.endpoints.createGroupPost + group.id,
      {},
    );

    if (response.ok) {
      listHandler.newToList(response, shares, setShares, null, true);
      setEditPost(response.data.post);
      setShowEdit(true);
    }
  };

  const createGroupPostFn = async (title, text, contentPillar) => {
    const response = await crudApi.justGet(
      MediaplannerGroupEndpoints.endpoints.createGroupPost + group.id,
    );
    if (response.ok) {
      const post = response.data.post;
      const edited = await crudApi.edit(SocialEndpoints.posts.edit, { id: post.id, title, text, content_pillar: contentPillar }, true);
      // the share came back before the edit: put the written post on the card,
      // not the blank one the group created
      const filled = edited?.ok && edited.data?.id ? edited.data : { ...post, title, text, content_pillar: contentPillar };
      listHandler.newToList({ ...response, data: { ...response.data, post: filled } }, shares, setShares, null, true);
      return { ok: true, data: filled };
    }
    return response;
  };

  const [viewMode, setViewMode] = useState("unpublished");
  const [publishedShares, setPublishedShares] = useState([]);
  const [publishedLoaded, setPublishedLoaded] = useState(false);

  useEffect(() => {
    if (viewMode !== "published" || publishedLoaded || !group?.id) return;
    crudApi.justGet(
      MediaplannerGroupEndpoints.endpoints.publishedPosts + group.id,
    ).then((response) => {
      if (response?.ok) {
        setPublishedShares(response.data ?? []);
      }
      setPublishedLoaded(true);
    });
  }, [viewMode, publishedLoaded, group?.id]);

  const tasks = useMemo(() => {
    const source = viewMode === "published" ? publishedShares : (shares ?? []);
    return source
      .filter((s) => {
        if (!s?.post) return false;
        if (viewMode === "published") return true;
        return s.post.status !== "published";
      })
      .map((s) => ({
        id: getShareId(s),
        post: s.post,
        share: s,
      }));
  }, [shares, publishedShares, getShareId, viewMode]);

  const [tagFilter, setTagFilter] = useState("");
  // Filter by author: "" = everyone, otherwise the author's username.
  const [userFilter, setUserFilter] = useState("");
  // Filter by approval state of the post's delegated schedules ("" = no filter).
  const [approvalFilter, setApprovalFilter] = useState("");
  const APPROVAL_FILTERS = [
    { value: "proposed", label: t("Backend.Groups.Board.Approval.Waiting", "Waiting for approval") },
    { value: "mine", label: t("Backend.Groups.Board.Approval.WaitingMine", "Waiting for my approval") },
    { value: "approved", label: t("Backend.Groups.Board.Approval.Approved", "Approved") },
    { value: "rejected", label: t("Backend.Groups.Board.Approval.Rejected", "Rejected") },
    { value: "cancelled", label: t("Backend.Groups.Board.Approval.Cancelled", "Cancelled") },
    { value: "none", label: t("Backend.Groups.Board.Approval.None", "Not planned for a member") },
  ];
  const [members, setMembers] = useState([]);

  // Every user in the group for the author dropdown. The members endpoint is
  // best effort (plain members may not be allowed to call it); authors found
  // on the board are always included so the list is never empty.
  useEffect(() => {
    if (!group?.id) return;
    let cancelled = false;
    crudApi
      .justGet(GroupConfig.endpoints.showMembers + group.id)
      .then((r) => {
        if (cancelled || !r.ok || !Array.isArray(r.data)) return;
        // Members come as UserGroup rows with a `user_info` summary (username, name).
        setMembers(r.data.map((m) => m?.user_info ?? m?.userinfo ?? m?.user ?? m).filter((u) => u && (u.username || u.id)));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [group?.id]);

  const groupUsers = useMemo(() => {
    const byUsername = new Map();
    const add = (u) => {
      if (!u?.username) return;
      const existing = byUsername.get(u.username) ?? {};
      byUsername.set(u.username, {
        username: u.username,
        id: existing.id ?? u.id ?? null,
        name: (existing.name && existing.name.trim()) || (u.name && u.name.trim()) || "",
      });
    };
    // Shares first: their author summary carries the full display name and id;
    // the members list only adds people without posts yet.
    (shares ?? []).forEach((sh) => add(sh?.userinfo));
    members.forEach(add);
    return [...byUsername.values()].sort((a, b) =>
      (a.name || a.username).localeCompare(b.name || b.username, undefined, { sensitivity: "base" })
    );
  }, [members, shares]);

  const [columns, setColumns] = useState({
    drafts: { name: "Drafts", tasks: [] },
    in_progress: { name: "In Progress", tasks: [] },
    ready: { name: "Ready", tasks: [] },
    planned: { name: "Scheduled", tasks: [] },
    published: { name: "Published", tasks: [] },
  });

  const filteredColumns = useMemo(() => {
    const query = tagFilter.trim().toLowerCase();
    if (!query && !userFilter && !approvalFilter) return columns;
    const matchesApproval = (task) => {
      if (!approvalFilter) return true;
      const delegated = ((task?.post ?? task)?.schedules ?? []).filter((sch) => sch?.publish_as);
      if (approvalFilter === "none") return delegated.length === 0;
      if (approvalFilter === "mine") {
        return delegated.some((sch) => sch.approval_status === "proposed" && sch.publish_as?.username === settings?.user?.username);
      }
      return delegated.some((sch) => sch.approval_status === approvalFilter);
    };
    const matchesTag = (task) => {
      if (!query) return true;
      const post = task?.post ?? task;
      const searchable = [
        ...(post?.tags ?? []).map((t) => t.name?.toLowerCase()),
        post?.post_type?.toLowerCase(),
        post?.content_type?.toLowerCase(),
        post?.content_pillar?.pillar?.toLowerCase(),
      ].filter(Boolean);
      return searchable.some((s) => s.includes(query));
    };
    const matchesUser = (task) => {
      if (!userFilter) return true;
      const share = task?.share ?? shares?.find((sh) => sh?.post?.id === (task?.post ?? task)?.id);
      return share?.userinfo?.username === userFilter;
    };
    return Object.fromEntries(
      Object.entries(columns).map(([colId, col]) => [
        colId,
        { ...col, tasks: (col.tasks ?? []).filter((task) => matchesTag(task) && matchesUser(task) && matchesApproval(task)) },
      ])
    );
  }, [columns, tagFilter, userFilter, approvalFilter, shares, settings]);

  // Only expose columns relevant to the active view mode.
  // This keeps published PostItems fully unmounted when not needed,
  // which is the main cause of edit slowness with large groups.
  const boardColumns = useMemo(() => {
    if (viewMode === "published") {
      return { published: filteredColumns.published };
    }
    return Object.fromEntries(
      Object.entries(filteredColumns).filter(([k]) => k !== "published")
    );
  }, [viewMode, filteredColumns]);

  useEffect(() => {
    const sorted = sortPostsByType(tasks);
    const safe = (colId) => sorted?.[colId]?.tasks ?? [];

    setColumns((prev) => ({
      ...prev,
      drafts: { ...prev.drafts, tasks: safe("drafts") },
      in_progress: { ...prev.in_progress, tasks: safe("in_progress") },
      ready: { ...prev.ready, tasks: safe("ready") },
      planned: { ...prev.planned, tasks: safe("planned") },
      published: { ...prev.published, tasks: safe("published") },
    }));
  }, [tasks]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateGroupPostStatus = async (post, targetColumnId) => {
    let url;

    switch (targetColumnId) {
      case "drafts":
        url = SocialEndpoints.posts.setDraft + post.id;
        break;
      case "in_progress":
        url = SocialEndpoints.posts.markInProgress + post.id;
        break;
      case "ready":
        url = SocialEndpoints.posts.setReady + post.id;
        break;
      default:
        return null;
    }

    return crudApi.justGet(url);
  };

  const onStatusChange = async (post, targetColumnId) => {
    const previous = shares;

    const nextStatus =
      targetColumnId === "drafts"
        ? "draft"
        : targetColumnId === "in_progress"
          ? "in_progress"
          : targetColumnId === "ready"
            ? "ready"
            : post.status;

    setShares((prev) =>
      (prev ?? []).map((s) =>
        s?.post?.id === post.id
          ? { ...s, post: { ...s.post, status: nextStatus } }
          : s,
      ),
    );

    try {
      const response = await updateGroupPostStatus(post, targetColumnId);

      if (!response?.ok) {
        setShares(previous);
        return;
      }

      if (response?.data) {
        setShares((prev) => replacePostInList(prev, response.data));
      }
    } catch (e) {
      console.error(e);
      setShares(previous);
    }
  };

  // Flatten visible board columns into a single list of posts for the Megaprompter
  // picker. Each post keeps the column it came from so the picker can filter by it.
  const megaprompterPosts = useMemo(() => {
    const out = [];
    for (const [columnId, col] of Object.entries(boardColumns ?? {})) {
      for (const task of col?.tasks ?? []) {
        const post = task?.post;
        if (post && post.text) {
          out.push({
            ...post,
            __groupName: group?.name,
            __columnId: columnId,
            __columnName: col?.name ?? columnId,
          });
        }
      }
    }
    return out;
  }, [boardColumns, group?.name]);

  // One menu component for the tab's lifetime (a new one per render would be a
  // new React type and close the open dropdown on every re-render); the
  // current handlers come through a ref.
  const menuHandlers = useRef({});
  menuHandlers.current = {
    group,
    rights,
    canEdit,
    shareOfTask,
    openPost,
    deletePost,
    removeShare: (t) => setShares((prev) => (prev ?? []).filter((s) => String(s?.id ?? s?.post?.id) !== String(t?.id))),
    teleprompt: (p) => { setTeleprompterPost(p); setShowTeleprompter(true); },
    share: (p) => { setSharePostId(p?.id); setShowShare(true); },
  };
  const ItemMenu = useMemo(
    () =>
      function GroupItemMenu({ post, task }) {
        const h = menuHandlers.current;
        return (
          <GroupPostMenu
            post={post}
            task={task}
            group={h.group}
            rights={h.rights}
            editable={h.canEdit(h.shareOfTask(post, task))}
            onEdit={(p) => h.openPost(p, task)}
            onDelete={() => h.deletePost?.(task)}
            onDeletePost={(t) => h.removeShare(t)}
            onTeleprompter={(p) => h.teleprompt(p)}
            onShare={(p) => h.share(p)}
          />
        );
      },
    []
  );

  return (
    <>
      <GroupPostViewModal
        show={showView}
        setShow={setShowView}
        share={viewShare}
        group={group}
      />
      <ShareModal show={showShare} setShow={setShowShare} sharePostId={sharePostId} />
      <TelepromterModal
        post={teleprompterPost}
        show={showTeleprompter}
        setShow={setShowTeleprompter}
      />
      <MegaprompterModal
        show={showMegaprompter}
        setShow={setShowMegaprompter}
        posts={megaprompterPosts}
        onPostUpdated={(postId, tags) => {
          const patch = (list) =>
            (list ?? []).map((item) =>
              item?.post?.id === postId
                ? { ...item, post: { ...item.post, tags } }
                : item
            );
          setShares(patch);
          setPublishedShares(patch);
        }}
      />
      <PostFormView
        showEdit={showEdit}
        setShowEdit={setShowEdit}
        editData={editPost}
        trigger={viewPostId}
        dataList={null}
        setTrigger={setViewPostId}
        customListHander={setDataList}
        share={editShare}
        group={group}
        pillarsEndpoint={MediaplannerGroupEndpoints.endpoints.pillars.list + group?.id}
      />

      <PostAssistant
        show={showAssistant}
        setShow={setShowAssistant}
        posts={shares}
        setPosts={setShares}
        setEditPost={setEditPost}
        setShowEdit={setShowEdit}
        showSpinner={showGenerating}
        setShowSpinner={setShowGenerating}
        createPostFn={createGroupPostFn}
        pillarsEndpoint={MediaplannerGroupEndpoints.endpoints.pillars.list + group?.id}
      />

      <Card>
        <Card.Header className="d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-center gap-2" style={{ background: "transparent", borderBottom: "1px solid #eaecf0", padding: "12px 16px" }}>
          <div style={{ display: "flex", gap: 4, background: "#f3f4f6", borderRadius: 8, padding: 3, alignSelf: "flex-start" }}>
            {[
              { key: "unpublished", label: t("Backend.Timeline.Unpublished", "Unpublished") },
              { key: "published",   label: t("Backend.Timeline.Published",   "Published") },
            ].map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => setViewMode(key)}
                style={{
                  fontSize: 12, fontWeight: 600, padding: "5px 14px", borderRadius: 6,
                  border: "none", cursor: "pointer", transition: "all 0.15s ease",
                  background: viewMode === key ? "#fff" : "transparent",
                  color: viewMode === key ? "#038edc" : "#6b7280",
                  boxShadow: viewMode === key ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
                }}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="d-flex align-items-center gap-2 justify-content-end">
            <button
              type="button"
              onClick={() => setShowMegaprompter(true)}
              title="Megaprompter — record multiple talking-head videos in a row"
              style={{
                padding: "6px 14px", borderRadius: 50, border: "none",
                background: "linear-gradient(135deg,#038edc,#0277a3)",
                color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer",
                display: "flex", alignItems: "center", gap: 6,
                boxShadow: "0 3px 10px rgba(3,142,220,0.3)",
              }}
            >
              <FontAwesomeIcon icon={faVideo} style={{ fontSize: 12 }} />
              <span className="d-none d-sm-inline">Megaprompter</span>
            </button>
            <button
              type="button"
              onClick={() => setShowAssistant(true)}
              title={t("Backend.Post.AIPost")}
              style={{
                padding: "6px 14px", borderRadius: 50, border: "none",
                background: "linear-gradient(135deg,#564ab1,#3d3490)",
                color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer",
                display: "flex", alignItems: "center", gap: 6,
                boxShadow: "0 3px 10px rgba(86,74,177,0.3)",
              }}
            >
              <FontAwesomeIcon icon={faWandMagicSparkles} style={{ fontSize: 12 }} />
              <span className="d-none d-sm-inline">{t("Backend.Post.AIPost")}</span>
            </button>
            <AddButton addFunction={onAdd} />
          </div>
        </Card.Header>
        <div style={{ padding: "8px 16px", borderBottom: "1px solid #eaecf0", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 600, flexShrink: 0 }}>Filter by tag:</span>
          <input
            type="text"
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
            placeholder="e.g. marketing, tips…"
            style={{ fontSize: 12, padding: "4px 10px", borderRadius: 8, border: "1.5px solid #e0e4ea", outline: "none", width: 220 }}
          />
          <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 600, flexShrink: 0, marginLeft: 8 }}>
            {t("Backend.Groups.Board.FilterByUser", "Filter by user:")}
          </span>
          <select
            id="group-board-user-filter"
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            style={{ fontSize: 12, padding: "4px 10px", borderRadius: 8, border: "1.5px solid #e0e4ea", outline: "none", minWidth: 180, background: "#fff", color: "#1a2236" }}
          >
            <option value="">{t("Backend.Groups.Board.AllUsers", "All users")}</option>
            {groupUsers.map((u) => (
              <option key={u.username} value={u.username}>
                {u.name || u.username}{u.username === settings?.user?.username ? ` (${t("Backend.Groups.Board.You", "you")})` : ""}
              </option>
            ))}
          </select>
          <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 600, flexShrink: 0, marginLeft: 8 }}>
            {t("Backend.Groups.Board.FilterByApproval", "Filter by approval:")}
          </span>
          <select
            id="group-board-approval-filter"
            value={approvalFilter}
            onChange={(e) => setApprovalFilter(e.target.value)}
            style={{ fontSize: 12, padding: "4px 10px", borderRadius: 8, border: "1.5px solid #e0e4ea", outline: "none", minWidth: 190, background: "#fff", color: "#1a2236" }}
          >
            <option value="">{t("Backend.Groups.Board.Approval.All", "All")}</option>
            {APPROVAL_FILTERS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {(tagFilter || userFilter || approvalFilter) && (
            <button
              type="button"
              onClick={() => { setTagFilter(""); setUserFilter(""); setApprovalFilter(""); }}
              style={{ fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 8, border: "1.5px solid #e0e4ea", background: "transparent", color: "#6b7280", cursor: "pointer" }}
            >
              Clear
            </button>
          )}
        </div>
        <CardBody>
          <GenericKanbanBoard
            columns={boardColumns}
            setColumns={setColumns}
            ItemMenu={ItemMenu}
            getItemProps={getItemProps}
            onStatusChange={onStatusChange}
            onEdit={(post, task) => openPost(post, task)}
          />
        </CardBody>
      </Card>
    </>
  );
}
